import React from 'react';
import moment from 'moment';
import R from 'ramda';
import injectCSS from 'react-jss';

const styles = {
  table: {
    borderTop: '1px solid #e0e0e0',
  },
  thead: {
    fontSize: '12px',
    fontWeight: 'bolder',
  },
  tr: {
    padding: '8px',
    borderBottom: '1px solid #e0e0e0',
  },
  flex: { display: 'flex' },
  flex1: {
    flex: 1,
  },
  colSpan5: {
    width: '60%',
  },
  indent: {
    paddingLeft: '16px',
  },
  dateRange: {
    padding: '8px',
    borderBottom: '1px solid #e0e0e0',
    color: 'rgba(0, 0, 0, 0.44)',
  },
  boldTxt: { fontWeight: 'bold' },
  total: {
    borderBottom: '1px solid #e0e0e0',
    padding: '8px',
  },
  totalTr: {
    padding: '4px',
  },
  flex2: {
    flex: 2,
  },
  lightText: {
    color: 'rgba(0, 0, 0, 0.44)',
  },
  alignRight: { textAlign: 'right' },
};

const InvoiceTable = ({
  invoice,
  classes: css,
}) => {
  if (!invoice) return <div/>;
  const dateFormat = 'DD MMM YYYY';
  const linesGroupedByPeriod = R.groupWith((a, b) =>
      R.path(['period', 'start'], a) === R.path(['period', 'start'], b)
      && R.path(['period', 'end'], a) === R.path(['period', 'end'], b), invoice.lines.data);
  return (
    <div className={css.table}>
      <div className={`${css.flex} ${css.tr} ${css.thead}`}>
        <div className={`${css.colSpan5}`}>DESCRIPTION</div>
        <div className={`${css.flex1}`}>QTY</div>
        <div className={`${css.flex1}`}>UNIT PRICE</div>
        <div className={`${css.flex1} ${css.alignRight}`}>AMOUNT</div>
      </div>
      {
        linesGroupedByPeriod.map((lines, i) => {
          const uniqProds = [];
          lines.forEach(line => {
            const { plan: linePlan } = line;
            let lineName = line.description
            || R.pathOr('', ['nickname'], linePlan)
            || R.pathOr('', ['name'], linePlan);
            if (linePlan.tiers) {
              linePlan.tiers.forEach((t, ti) => {
                const min = ti === 0 ? 1 : linePlan.tiers[ti - 1].up_to + 1;
                const amountMatch = (t.flat_amount && line.amount === t.flat_amount)
                  || (t.amount && (t.amount * line.quantity) === line.amount);
                if (amountMatch) {
                  if (ti === 0 && t.up_to) lineName = `First ${t.up_to}`;
                  else if (min && t.up_to) lineName = `Next ${min} to ${t.up_to}`;
                  else if (!t.up_to) lineName = `${min} and above`;
                }
              });
            }
            const obj = {
              amount: line.amount / 100,
              quantity: line.quantity,
              unitPrice: (() => {
                if (R.path(['price', 'unit_amount'], line)) return R.path(['price', 'unit_amount'], line) / 100;
                if (line.quantity) return line.amount / (line.quantity * 100);
                return null;
              })(),
              lineName,
            };
            const foundIndex = uniqProds.findIndex(p => p.name === linePlan.name);
            if (foundIndex > -1) {
              uniqProds[foundIndex].lines.push(obj);
            } else {
              uniqProds.push({
                ...linePlan,
                lines: [obj],
              });
            }
          });
          return (
            <div key={i}>
              <div
                className={`${css.dateRange}`}
              >
                {
                moment(
                  (
                    R.path([0, 'period', 'start'], lines)
                    || invoice.period_start
                  ) * 1000).format(dateFormat)
                } - {
                  moment(
                    (
                      R.path(['lines', 'data', 0, 'period', 'end'], invoice)
                      || invoice.period_end
                    ) * 1000).format(dateFormat)
                }
              </div>
              {
                uniqProds.map((prod, prodI) => (
                  <div key={prodI}>
                    <div className={`${css.flex} ${css.tr}`}>
                      <div
                        className={`${css.colSpan5} ${css.boldTxt}`}
                      >
                        {prod.name}
                      </div>
                      <div className={`${css.flex1}`}>
                        {/* {prod.lines.reduce((result, l) => result + l.quantity, 0)} */}
                      </div>
                      <div className={css.flex1}/>
                      <div className={`${css.flex1} ${css.alignRight}`}>
                        {(() => {
                          if (prod.lines.length === 1) {
                            return `$${R.path(['lines', 0, 'amount'], prod).toFixed(2)}`;
                          }
                        })()}
                      </div>
                    </div>
                    {
                      prod.lines.length > 1 && prod.lines.map((line, lineI) => (
                        <div key={lineI} className={`${css.flex} ${css.tr}`}>
                          <div className={`${css.colSpan5} ${css.indent}`}>
                            {
                              (() => {
                                const name = line.lineName || R.path(['lines', lineI + 1, 'lineName'], prod) || '';
                                if (line.amount && !line.unitPrice) {
                                  return `Flat fee for ${name.toLowerCase()}`;
                                }
                                return name;
                              })()
                            }
                          </div>
                          <div className={`${css.flex1}`}>
                            {line.quantity}
                          </div>
                          <div className={`${css.flex1}`}>
                            {!R.isNil(line.unitPrice) ? `$${line.unitPrice.toFixed(2)}` : ''}
                          </div>
                          <div className={`${css.flex1} ${css.alignRight}`}>
                            {!R.isNil(line.amount) ? `$${line.amount.toFixed(2)}` : ''}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ))
              }
            </div>
          );
        })
      }
      <div className={css.total}>
        <div className={`${css.flex} ${css.totalTr}`}>
          <div className={css.colSpan5}/>
          <div className={css.flex2}>
            Subtotal:
          </div>
          <div className={`${css.flex1} ${css.alignRight}`}>
            ${(invoice.subtotal / 100).toFixed(2)}
          </div>
        </div>
        {
          Boolean(invoice.discount) && (
            <div className={`${css.flex} ${css.totalTr}`}>
              <div className={css.colSpan5}/>
              <div className={`${css.flex2} ${css.lightText}`}>
                {R.path(['discount', 'coupon', 'id'], invoice)}
                ({
                R.path(['discount', 'coupon', 'amount_off'], invoice)
                ? `$${R.path(['discount', 'coupon', 'amount_off'], invoice) / 100} off`
                : `${R.path(['discount', 'coupon', 'percent_off'], invoice) / 100}% off`
                })
              </div>
              <div className={`${css.flex1} ${css.alignRight}`}>
                -${(R.pathOr(0, ['total_discount_amounts', 0, 'amount'], invoice) / 100).toFixed(2)}
              </div>
            </div>
          )
        }
        {
          Boolean(invoice.tax) && (
            <div className={`${css.flex} ${css.totalTr}`}>
              <div className={css.colSpan5}/>
              <div className={`${css.flex2} ${css.lightText}`}>
                Tax({R.path(['tax_percent'], invoice)}%)
              </div>
              <div className={`${css.flex1} ${css.alignRight}`}>
                ${(R.pathOr(0, ['tax'], invoice) / 100).toFixed(2)}
              </div>
            </div>
          )
        }
        <div className={`${css.flex} ${css.totalTr}`}>
          <div className={css.colSpan5}/>
          <div className={css.flex2}>
            Amount Due:
          </div>
          <div className={`${css.flex1} ${css.alignRight}`}>
            ${(invoice.amount_due / 100).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default injectCSS(styles)(InvoiceTable);



// WEBPACK FOOTER //
// ./src/app/components/settings/billing/invoice-table.jsx